import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import {
  Hammer,
  MessageCircle,
  Settings,
  MessageSquareCode,
  BookOpen,
  FlaskConical,
  Workflow,
  Anvil,
  Layers,
  ListTodo,
  SquareSlash,
  MessageCircleQuestionIcon,
} from "lucide-react";
import { usePostHog } from "posthog-js/react";

import { NavMain } from "@/components/sidebar/nav-main";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
} from "@/components/ui/sidebar";
import { usePreferencesStore } from "@/stores/preferences/preferences-provider";
import { MCPIcon } from "@/components/ui/mcp-icon";
import { SidebarUser } from "@/components/sidebar/sidebar-user";
import { useUpdateNotification } from "@/hooks/useUpdateNotification";
import { Button } from "@/components/ui/button";
import { HOSTED_MODE } from "@/lib/config";
import {
  listTools,
  type ListToolsResultWithMetadata,
} from "@/lib/apis/mcp-tools-api";
import {
  isMCPApp,
  isOpenAIApp,
  isOpenAIAppAndMCPApp,
} from "@/lib/mcp-ui/mcp-apps-utils";
import {
  isHostedSidebarTabAllowed,
  normalizeHostedHashTab,
} from "@/lib/hosted-tab-policy";
import type { ServerWithName } from "@/hooks/use-app-state";

// Define sections with their respective items
const navigationSections = [
  {
    id: "connection",
    items: [
      {
        title: "Servers",
        url: "#servers",
        icon: MCPIcon,
      },
      {
        title: "Chat",
        url: "#chat-v2",
        icon: MessageCircle,
      },
    ],
  },
  {
    id: "mcp-apps",
    items: [
      {
        title: "App Builder",
        url: "#app-builder",
        icon: Anvil,
      },
      {
        title: "Views",
        url: "#views",
        icon: Layers,
      },
      {
        title: "Test Cases",
        url: "#evals",
        icon: FlaskConical,
      },
    ],
  },
  {
    id: "others",
    items: [
      {
        title: "Skills",
        url: "#skills",
        icon: SquareSlash,
      },
      {
        title: "OAuth Debugger",
        url: "#oauth-flow",
        icon: Workflow,
      },
      // {
      //   title: "Tracing",
      //   url: "#tracing",
      //   icon: Activity,
      // },
    ],
  },
  {
    id: "primitives",
    items: [
      {
        title: "Tools",
        url: "#tools",
        icon: Hammer,
      },
      {
        title: "Resources",
        url: "#resources",
        icon: BookOpen,
      },
      {
        title: "Prompts",
        url: "#prompts",
        icon: MessageSquareCode,
      },
      {
        title: "Tasks",
        url: "#tasks",
        icon: ListTodo,
      },
    ],
  },
  {
    id: "settings",
    items: [
      {
        title: "Support",
        url: "#support",
        icon: MessageCircleQuestionIcon,
      },
      {
        title: "Settings",
        url: "#settings",
        icon: Settings,
      },
    ],
  },
];

const hostedNavigationSections = navigationSections
  .map((section) => ({
    ...section,
    items: section.items.filter((item) =>
      isHostedSidebarTabAllowed(
        normalizeHostedHashTab(
          item.url.startsWith("#") ? item.url.slice(1) : item.url,
        ),
      ),
    ),
  }))
  .filter((section) => section.items.length > 0);

interface MCPSidebarProps extends React.ComponentProps<typeof Sidebar> {
  onNavigate?: (section: string) => void;
  activeTab?: string;
  /** Servers to check for app capabilities */
  servers?: Record<string, ServerWithName>;
}

const APP_BUILDER_VISITED_KEY = "mcp-app-builder-visited";

export function MCPSidebar({
  onNavigate,
  activeTab,
  servers = {},
  ...props
}: MCPSidebarProps) {
  const posthog = usePostHog();
  const themeMode = usePreferencesStore((s) => s.themeMode);
  const { updateReady, restartAndInstall } = useUpdateNotification();
  const [toolsDataMap, setToolsDataMap] = useState<
    Record<string, ListToolsResultWithMetadata | null>
  >({});
  const [hasVisitedAppBuilder, setHasVisitedAppBuilder] = useState(() => {
    return localStorage.getItem(APP_BUILDER_VISITED_KEY) === "true";
  });

  // Get list of connected server names
  const connectedServerNames = useMemo(() => {
    return Object.entries(servers)
      .filter(([, server]) => server.connectionStatus === "connected")
      .map(([name]) => name);
  }, [servers]);

  // Fetch tools data for connected servers
  useEffect(() => {
    const fetchToolsData = async () => {
      if (connectedServerNames.length === 0) {
        setToolsDataMap({});
        return;
      }

      const newToolsDataMap: Record<
        string,
        ListToolsResultWithMetadata | null
      > = {};

      await Promise.all(
        connectedServerNames.map(async (serverName) => {
          try {
            const result = await listTools({ serverId: serverName });
            newToolsDataMap[serverName] = result;
          } catch {
            newToolsDataMap[serverName] = null;
          }
        }),
      );

      setToolsDataMap(newToolsDataMap);
    };

    fetchToolsData();
  }, [connectedServerNames.join(",")]);

  // Check if any connected server is an app
  const hasAppServer = useMemo(() => {
    return Object.values(toolsDataMap).some(
      (toolsData) =>
        isMCPApp(toolsData) ||
        isOpenAIApp(toolsData) ||
        isOpenAIAppAndMCPApp(toolsData),
    );
  }, [toolsDataMap]);

  const showAppBuilderBubble =
    hasAppServer && activeTab !== "app-builder" && !hasVisitedAppBuilder;

  const handleNavClick = (url: string) => {
    if (onNavigate && url.startsWith("#")) {
      const section = url.slice(1);
      // Mark App Builder as visited when clicked (always, not just when bubble is visible)
      if (section === "app-builder" && showAppBuilderBubble) {
        localStorage.setItem(APP_BUILDER_VISITED_KEY, "true");
        setHasVisitedAppBuilder(true);
      }
      // Track skills tab opened
      if (section === "skills") {
        posthog.capture("skills_tab_opened");
      }
      onNavigate(section);
    } else {
      window.open(url, "_blank");
    }
  };

  const appBuilderBubble = showAppBuilderBubble
    ? {
        message: "Build your UI app with App Builder.",
        subMessage: "Get started",
      }
    : null;
  const visibleNavigationSections = HOSTED_MODE
    ? hostedNavigationSections
    : navigationSections;

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <button
          onClick={() => handleNavClick("#servers")}
          className="flex items-center justify-center px-4 py-4 w-full cursor-pointer hover:opacity-80 transition-opacity"
        >
          <img
            src={
              themeMode === "dark" ? "/mcp_jam_dark.png" : "/mcp_jam_light.png"
            }
            alt="MCP Jam"
            className="h-4 w-auto"
          />
        </button>
        {updateReady && (
          <div className="px-2 pb-2">
            <Button
              size="sm"
              onClick={restartAndInstall}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground h-7 text-xs font-medium rounded-md"
            >
              Update & Restart
            </Button>
          </div>
        )}
      </SidebarHeader>
      <SidebarContent>
        {visibleNavigationSections.map((section, sectionIndex) => (
          <React.Fragment key={section.id}>
            <NavMain
              items={section.items.map((item) => ({
                ...item,
                isActive: item.url === `#${activeTab}`,
              }))}
              onItemClick={handleNavClick}
              appBuilderBubble={
                section.id === "mcp-apps" ? appBuilderBubble : null
              }
            />
            {/* Add subtle divider between sections (except after the last section) */}
            {sectionIndex < visibleNavigationSections.length - 1 && (
              <div className="mx-4 my-2 border-t border-border/50" />
            )}
          </React.Fragment>
        ))}
      </SidebarContent>
      <SidebarFooter>
        <SidebarUser />
      </SidebarFooter>
    </Sidebar>
  );
}
